import Phaser from 'phaser';
import { SEAT_RISE, SCOOP_STACK_DY, BASE_SEAT_ORIGIN } from '../config.ts';
import { FLAVOR_BY_ID } from '../../assets/palette.ts';
import { CountDots } from './CountDots.ts';
import type { Order } from '../levels.ts';

const PREVIEW_SCOOP_W = 132;
const PREVIEW_SCOOP_H = PREVIEW_SCOOP_W * (175 / 200);

/**
 * The customer's wish, drawn entirely in pictures inside a speech bubble: a
 * little preview of the exact ice cream they want, plus dots showing the total
 * number of scoops. No text — a pre-reader reads the picture.
 */
export class OrderBubble extends Phaser.GameObjects.Container {
  private bubble: Phaser.GameObjects.Image;
  private preview: Phaser.GameObjects.Container | null = null;
  private dots: CountDots;
  /** Resting scale (lets the bubble be sized down without re-laying-out parts). */
  private readonly baseScale: number;

  constructor(scene: Phaser.Scene, x: number, y: number, baseScale = 1) {
    super(scene, x, y);
    this.baseScale = baseScale;
    this.bubble = scene.add.image(0, 0, 'bubble').setDisplaySize(360, 290);
    this.add(this.bubble);
    this.dots = new CountDots(scene, 0, 72);
    this.add(this.dots);
    scene.add.existing(this);
    this.setScale(0);
  }

  show(order: Order): void {
    if (this.preview) this.preview.destroy();
    this.preview = this.buildPreview(order);
    this.add(this.preview);
    this.bringToTop(this.dots);

    const flavor = order.scoops[0]?.flavor;
    const color = flavor ? Phaser.Display.Color.HexStringToColor(FLAVOR_BY_ID[flavor].color).color : 0x4a3f3a;
    this.dots.setColor(color);
    this.dots.setCount(order.total);

    // Pop the bubble in.
    this.scene.tweens.add({
      targets: this,
      scale: this.baseScale,
      duration: 420,
      ease: 'Back.Out',
    });
  }

  hide(onDone?: () => void): void {
    this.scene.tweens.add({
      targets: this,
      scale: 0,
      duration: 240,
      ease: 'Back.In',
      onComplete: () => onDone?.(),
    });
  }

  /** A small static stack mirroring the order, auto-scaled to fit the bubble. */
  private buildPreview(order: Order): Phaser.GameObjects.Container {
    const c = this.scene.add.container(0, -56);

    const baseKey = order.base === 'cup' ? 'base_cup' : 'base_cone';
    const baseW = order.base === 'cup' ? 170 : 160;
    const baseH = order.base === 'cup' ? 190 : 200;
    const base = this.scene.add
      .image(0, 0, baseKey)
      .setDisplaySize(baseW, baseH)
      .setOrigin(0.5, BASE_SEAT_ORIGIN[order.base]);
    c.add(base);

    // Flatten the order into a bottom-up list of scoop flavors.
    const flavors: string[] = [];
    for (const r of order.scoops) for (let i = 0; i < r.count; i++) flavors.push(r.flavor);

    let topY = -SEAT_RISE;
    flavors.forEach((f, i) => {
      const y = -SEAT_RISE - i * SCOOP_STACK_DY;
      topY = y;
      const s = this.scene.add.image(0, y, `scoop_${f}`).setDisplaySize(PREVIEW_SCOOP_W, PREVIEW_SCOOP_H);
      c.add(s);
    });

    // Fit the whole stack within the bubble's picture area.
    const baseBottom = baseH * (1 - BASE_SEAT_ORIGIN[order.base]);
    const stackTop = topY - PREVIEW_SCOOP_H / 2;
    const totalH = baseBottom - stackTop;
    const scale = Math.min(168 / totalH, 0.62);
    c.setScale(scale);
    // Vertically center the scaled stack in the upper picture region.
    const midY = (stackTop + baseBottom) / 2;
    c.setY(-44 - midY * scale);
    return c;
  }
}
